import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { ProductService } from './product.service';
import { InMemoryStorageService } from '../../services/in-memory-storage.service';

export interface RecommendationAuditSummary {
  totalAuditedLines: number;
  distinctOrderCount: number;
  recommendationSelectCount: number;
  manualCount: number;
  copyCount: number;
  excelCount: number;
  otherSourceCount: number;
  wasRecommendedTrue: number;
  wasRecommendedFalse: number;
  wasRecommendedNull: number;
  wasRecommendedNoReco: number;
  selectSharePercent: number;
  overlapRatePercent: number;
}

export interface RecommendationAuditDaily {
  requestedDate: string;
  totalAuditedLines: number;
  recommendationSelectCount: number;
  manualCount: number;
  copyCount: number;
  excelCount: number;
  wasRecommendedTrue: number;
  wasRecommendedFalse: number;
  wasRecommendedNoReco?: number;
}

export interface RecommendationAuditSalesperson {
  userIdResponsible: string;
  totalAuditedLines: number;
  recommendationSelectCount: number;
  manualCount: number;
  copyCount: number;
  excelCount: number;
  wasRecommendedTrue: number;
  wasRecommendedFalse: number;
  wasRecommendedNoReco?: number;
}

export interface RecommendationAuditDashboard {
  summary: RecommendationAuditSummary;
  daily: RecommendationAuditDaily[];
  bySalesperson: RecommendationAuditSalesperson[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(
    private http: HttpClient,
    private productService: ProductService,
    private inMemoryStorage: InMemoryStorageService
  ) { }

  getSalesReport(fromDate: string, toDate: string) {
    const url = `${environment.apiUrl}dashboard/GetSalesReport`;
    return this.http.get<any[]>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    });
  }

  getTopFiveItemsSold(fromDate: string, toDate: string) {
    const url = `${environment.apiUrl}dashboard/GetTopFiveItemsSold`;
    return this.http.get<any[]>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    });
  }

  getLeastFiveItemsSold(fromDate: string, toDate: string) {
    const url = `${environment.apiUrl}dashboard/GetLeastFiveItemsSold`;
    return this.http.get<any[]>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    });
  }

  getTopFiveCustomersBySales(fromDate: string, toDate: string) {
    const url = `${environment.apiUrl}dashboard/GetTopFiveCustomersBySales`;
    return this.http.get<any[]>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    }).pipe(
      catchError(error => {
        console.error('Error in getTopFiveCustomersBySales:', error);
        return throwError(() => error);
      })
    );
  }

  getLeastFiveCustomersBySales(fromDate: string, toDate: string) {
    const url = `${environment.apiUrl}dashboard/GetLeastFiveCustomersBySales`;
    return this.http.get<any[]>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    }).pipe(
      catchError(error => {
        console.error('Error in getLeastFiveCustomersBySales:', error);
        return throwError(() => error);
      })
    );
  }

  getUserDashboard() {
    const company = this.inMemoryStorage.get('company');
    const division = this.inMemoryStorage.get('division');
    const username = this.inMemoryStorage.get('userName') || '';
    const url = `${environment.apiUrl}dashboard/UserDashboard`;
    return this.http.get<any>(url, {
      params: {
        username,
        company,
        division
      }
    });
  }

  getSalesRanking(apiUrl: string) {
    return this.http.get(apiUrl);
  }

  loadSalesAchievementsData(fromDate: string, toDate: string) {
    const company = this.inMemoryStorage.get('company');
    const division = this.inMemoryStorage.get('division');
    const apiUrl = `${environment.apiUrl}dashboard/GetSalesReport/AllSalesPerson?company=${company}&division=${division}&fromDate=${fromDate}&toDate=${toDate}`;
    return this.http.get<any[]>(apiUrl);
  }

  getRecommendationAuditDashboard(fromDate: string, toDate: string): Observable<RecommendationAuditDashboard> {
    const url = `${environment.apiUrl}dashboard/GetRecommendationAuditDashboard`;
    return this.http.get<RecommendationAuditDashboard>(url, {
      params: {
        fromDate,
        toDate,
        company: this.inMemoryStorage.get('company'),
        division: this.inMemoryStorage.get('division')
      }
    });
  }

  loadM3SalesAchievementsData(fromDate: string, toDate: string) {
    const company = this.inMemoryStorage.get('company');
    const division = this.inMemoryStorage.get('division');
    const apiUrl = `${environment.apiUrl}dashboard/GetSalesReport/ByM3SalesPerson?company=${company}&division=${division}&fromDate=${fromDate}&toDate=${toDate}`;
    return this.http.get<any[]>(apiUrl);
  }
}
